import type { ReactNode } from 'react';
import { Loader2, Pencil, Save, X } from 'lucide-react';

export interface EditBarProps {
  editing: boolean;
  dirty: boolean;
  saving?: boolean;
  /** false = kein „Bearbeiten" im Lesemodus */
  canEdit?: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  saveLabel?: string;
  /** Anzahl der Änderungen für „Speichern (n)" */
  changeCount?: number;
  /** Weitere Knöpfe, links von den Standard-Knöpfen */
  extra?: ReactNode;
  /** Leiste klebt am unteren Rand (lange Formulare) */
  sticky?: boolean;
  testAnchor?: string;
}

const BTN = 'inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg min-h-[44px] text-sm disabled:opacity-50';

/**
 * Knopfleiste eines Dokuments (Regel „Dokument-Navigation" R3 + R4): im Lesemodus
 * „Bearbeiten", im Bearbeitungsmodus immer „Abbrechen" und „Speichern".
 *
 * „Speichern" bleibt auch ohne Änderung bedienbar: Es führt dann ohne Schreibaufruf
 * zurück in den Lesemodus.
 */
export function EditBar({
  editing, dirty, saving = false, canEdit = true,
  onEdit, onSave, onCancel,
  saveLabel = 'Speichern', changeCount, extra, sticky = false, testAnchor = 'edit-bar',
}: EditBarProps) {
  const wrap = sticky
    ? 'sticky bottom-0 z-30 -mx-4 px-4 py-3 bg-slate-950/95 border-t border-slate-800 flex flex-wrap items-center justify-end gap-2'
    : 'flex flex-wrap items-center justify-end gap-2';

  if (!editing) {
    if (!canEdit && !extra) return null;
    return (
      <div className={wrap} data-test-anchor={testAnchor} data-mode="read">
        {extra}
        {canEdit && (
          <button type="button" onClick={onEdit} className={`${BTN} bg-slate-800 text-slate-200 hover:bg-slate-700`} data-action="edit">
            <Pencil size={14} />
            Bearbeiten
          </button>
        )}
      </div>
    );
  }

  const count = typeof changeCount === 'number' && changeCount > 0 ? ` (${changeCount})` : '';
  return (
    <div className={wrap} data-test-anchor={testAnchor} data-mode="edit">
      {dirty && (
        <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 whitespace-nowrap" data-state="dirty">
          ungespeichert
        </span>
      )}
      {extra}
      <button type="button" onClick={onCancel} disabled={saving} className={`${BTN} text-slate-300 hover:bg-slate-800`} data-action="cancel">
        <X size={14} />
        Abbrechen
      </button>
      <button type="button" onClick={onSave} disabled={saving} className={`${BTN} bg-brand-600 hover:bg-brand-500 text-primary-foreground`} data-action="save">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        {saving ? 'Speichere …' : `${saveLabel}${count}`}
      </button>
    </div>
  );
}
